import {
  DRAWER_SCAN_SUBMIT_INTENTS,
  DRAWER_SCAN_SUBMIT_ROUTES,
} from '../constants/drawerScanSubmitIntents.const';
import { SUBMIT_ORDER_DRAWER_SCAN_ACTION_NAME } from '../constants/scanActionNames.const';
import { SCAN_SUBMIT_TARGET_REGISTRY } from '../constants/scanSubmitTargetRegistry.const';
import {
  type CreateSubmitOrderDrawerScanActionOptions,
  type DrawerScanSubmitIntent,
} from '../types/drawerScanSubmit.type';
import { type ScanAction, type ScanActionResult } from '../types/scanAction.type';
import {
  type ScanBarcodeContent,
  type ScanContext,
  type ScanUiContext,
} from '../types/scanContext.type';
import { type ScanEvent } from '../types/scanEvent.type';
import { type ScanSubmitTarget } from '../types/scanSubmitTarget.type';
import { matchesScannedOrderShipment } from '../utils/matchesScannedOrderShipment.util';
import { resolveDrawerScanSubmitIntent } from '../utils/resolveDrawerScanSubmitIntent.util';

interface DrawerScanSubmitStep {
  barcodeContent: ScanBarcodeContent;
  intent: DrawerScanSubmitIntent;
}

const selectionFromUiContext = (ui: ScanUiContext) => ({
  batchEntryId: ui.selected_shipment_id,
  orderObjectId: ui.selected_order_object_id,
  transactionObjectId: ui.selected_transaction_object_id,
});

/**
 * The step the scan asks for, when a drawer stands open on the scanned order
 * shipment. Resolved from the UI context alone, so a scan the open drawer is
 * plainly about still belongs to this action while its button is busy or still
 * loading - it just has nothing to submit yet.
 */
const resolveStep = ({
  barcode_content: barcodeContent,
  route,
  ui,
}: ScanContext): DrawerScanSubmitStep | undefined => {
  if (!barcodeContent) return undefined;
  if (!DRAWER_SCAN_SUBMIT_ROUTES.some((allowedRoute) => allowedRoute === route.route))
    return undefined;
  if (!ui.open_drawer) return undefined;

  const intent = resolveDrawerScanSubmitIntent(ui.open_drawer_name);

  if (!intent) return undefined;
  if (!matchesScannedOrderShipment(selectionFromUiContext(ui), barcodeContent)) return undefined;

  return { barcodeContent, intent };
};

/**
 * Performs the primary action of the drawer standing open on the scanned order
 * shipment: advancing the order details drawer to Buy & Print, buying and printing
 * from there, or printing an already purchased label from the shipment details
 * drawer. Which step a scan reaches is whichever one the open drawer published, so
 * a scan can never do what a click on that drawer's button cannot.
 *
 * The label purchased drawer is claimed too, and does nothing: the purchase it
 * reports is the end of the flow, and reporting a re-scan there as an unresolved
 * scan would show the operator an error for something that just succeeded.
 */
export const createSubmitOrderDrawerScanAction = ({
  registry = SCAN_SUBMIT_TARGET_REGISTRY,
}: CreateSubmitOrderDrawerScanActionOptions = {}): ScanAction => {
  const findTarget = ({ barcodeContent, intent }: DrawerScanSubmitStep) =>
    registry
      .snapshot()
      .find(
        (target: ScanSubmitTarget) =>
          target.intent === intent && matchesScannedOrderShipment(target, barcodeContent),
      );

  return {
    execute: (scanEvent: ScanEvent): ScanActionResult => {
      const step = resolveStep(scanEvent.scan_context);

      if (!step) return { success: true };

      // Nothing is left to submit once the label is bought, and no surface offers
      // a step for it, so stop before looking one up.
      if (step.intent === DRAWER_SCAN_SUBMIT_INTENTS.ALREADY_PURCHASED) return { success: true };

      const target = findTarget(step);

      // The drawer publishes nothing while it loads or purchases: leave it alone.
      if (!target || target.isSubmitting) return { success: true };

      if (!target.canSubmit) {
        if (target.blocker) target.onBlocked(target.blocker);

        return { success: true };
      }

      target.submit();

      return { success: true };
    },

    matches: (scanEvent: ScanEvent): boolean => Boolean(resolveStep(scanEvent.scan_context)),

    name: SUBMIT_ORDER_DRAWER_SCAN_ACTION_NAME,
  };
};
